import axios from 'axios';
import tokenService from '../services/tokenService';

let onUnauthorized = null;

export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

function resolveBaseUrl() {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  const { hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:5000';
  }
  return '';
}

export const apiClient = axios.create({
  baseURL: resolveBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
  timeout: 60000,
});

apiClient.interceptors.request.use((config) => {
  const token = tokenService.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && onUnauthorized) {
      onUnauthorized(error.response.data?.message || 'Session expired');
    }
    return Promise.reject(error);
  }
);

export async function apiGet(path, config) {
  const res = await apiClient.get(path, config);
  return res.data;
}

export async function apiPost(path, data, config) {
  const res = await apiClient.post(path, data, config);
  return res.data;
}

export async function apiPut(path, data, config) {
  const res = await apiClient.put(path, data, config);
  return res.data;
}

export async function apiPatch(path, data, config) {
  const res = await apiClient.patch(path, data, config);
  return res.data;
}

export async function apiDelete(path, config) {
  const res = await apiClient.delete(path, config);
  return res.data;
}

/** Drop-in fetch replacement using axios + auth interceptors */
export async function apiFetch(path, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const config = {
    url: path,
    method,
    signal: options.signal,
    headers: options.headers,
  };
  if (options.body) {
    config.data = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
  }
  try {
    const res = await apiClient.request(config);
    return {
      ok: true,
      status: res.status,
      json: async () => res.data,
      text: async () => (typeof res.data === 'string' ? res.data : JSON.stringify(res.data)),
    };
  } catch (error) {
    if (error.response) {
      const body = error.response.data;
      return {
        ok: false,
        status: error.response.status,
        json: async () => body,
        text: async () => (typeof body === 'string' ? body : JSON.stringify(body)),
      };
    }
    throw error;
  }
}

export function getApiBaseUrl() {
  return resolveBaseUrl();
}
